import React, { createRef, useEffect, useState } from 'react';
import { useHistory } from "react-router-dom";

import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import CloseIcon from '@material-ui/icons/Close';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Snackbar from '@material-ui/core/Snackbar';

import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles(theme => ({
    paper: {
        padding: theme.spacing(3),
    },
    input: {
        display: 'none'
    },
    inputField: {
        '& .MuiTextField-root': {
            width: '40ch',
        },
    },
    textMessageInputField: {
        '& .MuiTextField-root': {
            width: '80ch',
        },
    },
    loader: {
        height: 45
    }
}));

const TextMessagingStart = () => {
    useEffect(() => {
        document.title = 'Text Messaging Start';
        getPhoneNumbers();
    }, []);

    const [phoneNumbers, setPhoneNumbers] = useState([]);

    const getPhoneNumbers = () => {
        fetch('/admin/sms/phone-numbers', { method: 'GET' })
        .then(response => {
            return response.json();
        })
        .then(result => {
            setPhoneNumbers(result);
        })
        .catch(reason => {
            alert('An error prevented getting phone number senders.');
        })
    };

    const classes = useStyles();
    const ref = createRef();
    const history = useHistory();
    const [columns, setColumns] = useState([]);
    const [selectedColumn, setSelectedColumn] = useState('');
    const [displayNameColumn, setDisplayNameColumn] = useState('');
    const [selectedPhoneNumber, setSelectedPhoneNumber] = useState('');
    const [loading, setLoading] = useState(false);
    const [fileUpload, setFileUpload] = useState(null);
    const [messageField, setMessageField] = useState('');
    const [fileInfo, setFileInfo] = useState('');

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    const handleOnClick = () => {
        ref.current.click();
    }

    const handleFileClick = (e) => {
        e.target.value = null;
        handleClear();
    }

    const handleUpload = (event) => {
        if (!event.target.value){
            return;
        }
        const fileSize = event.target.files[0].size / 1024 / 1024;
        if (fileSize > 5.0) {
            alert(`Please try a file smaller than 5.0 MB. Actual size: ${fileSize.toFixed(2)} MB`);
            return;
        }
        setLoading(true);
        const formData = new FormData();
        formData.append('_token', window._token);
        formData.append('csv', event.target.files[0]);
        setFileUpload(event.target.files[0]);
        let url = `/admin/sms/start`;
        fetch(url, {
            method: 'POST',
            body: formData
        })
        .then(response => {
            return response.json();
        })
        .then(result => {
            if (result.columns) {
                setColumns(result.columns);
            }
            if (result.fileInfo) {
                setFileInfo(result.fileInfo);
            }
            setLoading(false);
        })
        .catch(reason => {
            setLoading(false);
            alert('An error prevented loading the CSV. Please try a different file (CSV format only)');
        })
    }

    const handleCancel = () => {
        history.push('/admin/sms');
    }

    const handleClear = () => {
        setSelectedColumn('');
        setDisplayNameColumn('');
        setFileUpload(null);
        setFileInfo('');
        setColumns([]);
    }

    const handleColumnChange = (event) => {
        setSelectedColumn(event.target.value);
    }

    const handleDisplayNameChange = (event) => {
        setDisplayNameColumn(event.target.value);
    }

    const handleMessageChange = (event) => {
        setMessageField(event.target.value);
    }

    const handlePhoneNumberChange = (event) => {
        setSelectedPhoneNumber(event.target.value);
    }

    const handleSubmit = () => {
        if (!messageField || !selectedColumn || !fileUpload) {
            alert('Missing one of the following: message, selected column, file upload');
            return;
        }
        setLoading(true);
        const formData = new FormData();
        formData.append('_token', window._token);
        formData.append('csv', fileUpload);
        formData.append('selectedColumn', selectedColumn);
        formData.append('displayNameColumn', displayNameColumn);
        formData.append('phoneNumberId', selectedPhoneNumber);
        formData.append('messageField', messageField);
        formData.append('filename', fileUpload.name);
        let url = `/admin/sms/start`;
        fetch(url, {
            method: 'POST',
            body: formData
        })
        .then(response => {
            return response.json();
        })
        .then(result => {
            console.log(result);
            setLoading(false);
            setMessageField('');
            setSnackbarMessage('Successfully sent message');
            setOpenSnackbar(true);
        })
        .then(() => {
            handleClear();
        })
        .catch(reason => {
            setLoading(false);
            alert('An error prevented submitting. Please try again.');
        })
    }

    const hasColumns = fileUpload !== null && columns !== null && columns.length > 0;

    return (
        <Paper square className={classes.paper}>
            <Typography variant="h6">Text Messaging</Typography>
            <Box mt={2}>
                <Typography variant="subtitle2">1. Upload a CSV</Typography>
            </Box>
            <Box mt={1} display="flex">
                <Button color="primary" variant="contained" onClick={handleOnClick} startIcon={<CloudUploadIcon />}>
                    Choose File
                </Button>
                <Box ml={1}>
                    <Button onClick={handleClear}>Reset</Button>
                </Box>
            </Box>
            <Box mt={2}>
                {fileUpload && <Typography variant="subtitle2" color="textSecondary">{fileUpload.name}</Typography>}
                {fileInfo && <Typography variant="subtitle2" color="textSecondary">{fileInfo} rows in this file</Typography>}
            </Box>
            <input
                accept=".csv"
                className={classes.input}
                id="file"
                multiple
                type="file"
                ref={ref}
                onChange={handleUpload}
                onClick={handleFileClick}
            />
            <Box className={classes.inputField}>
                <Box mt={2} mb={1}>
                    <Typography variant="subtitle2">2. Select Phone Number Column</Typography>
                </Box>
                <TextField
                    disabled={!hasColumns}
                    value={selectedColumn}
                    onChange={handleColumnChange}
                    select
                    helperText="Please select phone number column from the CSV file"
                    variant="outlined"
                >
                    {hasColumns && (
                        columns.map((option, index) => (
                            <MenuItem value={index} key={`option-${index}`}>{option}</MenuItem>
                        ))
                    )}
                    {!hasColumns && <MenuItem>---</MenuItem>}
                </TextField>
            </Box>
            <Box className={classes.inputField}>
                <Box mt={2} mb={1}>
                    <Typography variant="subtitle2">3. Select Display Name Column (Optional)</Typography>
                </Box>
                <TextField
                    disabled={!hasColumns}
                    value={displayNameColumn}
                    onChange={handleDisplayNameChange}
                    select
                    helperText="Please select display name column from the CSV file"
                    variant="outlined"
                >
                    {hasColumns && (
                        columns.map((option, index) => (
                            <MenuItem value={index} key={`option-${index}`}>{option}</MenuItem>
                        ))
                    )}
                    {!hasColumns && <MenuItem>---</MenuItem>}
                </TextField>
            </Box>
            <Box className={classes.inputField}>
                <Box mt={2} mb={1}>
                    <Typography variant="subtitle2">4. Set Phone Number for Sender</Typography>
                </Box>
                <TextField
                    disabled={!hasColumns}
                    value={selectedPhoneNumber}
                    onChange={handlePhoneNumberChange}
                    select
                    helperText="This is the sender phone number"
                    variant="outlined"
                >
                    {phoneNumbers && phoneNumbers.length > 0 && (
                        phoneNumbers.map((option, index) => (
                            <MenuItem value={option.id} key={`phonenumber-${index}`}>{option.friendly_name}</MenuItem>
                        ))
                    )}
                    {!phoneNumbers && <MenuItem>---</MenuItem>}
                </TextField>
            </Box>
            <Box className={classes.textMessageInputField}>
                <Box mt={2} mb={1}>
                    <Typography variant="subtitle2">5. Enter SMS Message</Typography>
                </Box>
                <TextField
                    disabled={!hasColumns || !selectedColumn}
                    value={messageField}
                    onChange={handleMessageChange}
                    placeholder="Enter text message"
                    variant="outlined"
                    multiline
                    rows={8}
                    helperText="To personalize the message with the display name, include {display_name} as a placeholder"
                />
            </Box>
            <Box mt={2} display="flex">
                <Button disabled={!hasColumns || !messageField || !selectedColumn || !selectedPhoneNumber || loading} variant="contained" color="primary" onClick={handleSubmit}>Submit</Button>
                <Box ml={1}>
                    <Button disabled={loading} variant="outlined" onClick={handleCancel}>Back</Button>
                </Box>
            </Box>
            <Box mt={2} className={classes.loader}>
                {loading && <CircularProgress color="primary" />}
            </Box>
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                open={openSnackbar}
                autoHideDuration={8000}
                onClose={handleCloseSnackbar}
                message={snackbarMessage}
                action={
                    <React.Fragment>
                        <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseSnackbar}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </React.Fragment>
                }
            />
        </Paper>
    );
}

export default TextMessagingStart;
